import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './ImprovedDatePicker.css';

interface ImprovedDatePickerProps {
  id: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  hasError?: boolean;
}

const ImprovedDatePicker: React.FC<ImprovedDatePickerProps> = ({
  id,
  value,
  onChange,
  className = '',
  required = false,
  disabled = false,
  placeholder = 'mm/dd/yyyy',
  style,
  hasError = false
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Convert YYYY-MM-DD string to Date object
  const stringToDate = (dateString: string): Date | null => {
    if (!dateString || dateString === '') return null;
    
    // If it's in YYYY-MM-DD format, create a Date object
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    
    return null;
  };

  // Convert Date object to YYYY-MM-DD string
  const dateToString = (date: Date | null): string => {
    if (!date) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  // Update internal date value when prop value changes
  useEffect(() => {
    setSelectedDate(stringToDate(value));
  }, [value]);

  // Handle date picker change
  const handleDateChange = (newDate: Date | null) => {
    setSelectedDate(newDate);
    
    // Convert to string and trigger onChange
    const dateString = dateToString(newDate);
    const syntheticEvent = {
      target: {
        value: dateString,
        id: id
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    onChange(syntheticEvent);
  };

  const eighteenYearsAgo = new Date();
  eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);

  return (
    <div className={className}>
      <DatePicker 
        selected={selectedDate} 
        onChange={handleDateChange}
        dateFormat="MM/dd/yyyy"
        maxDate={eighteenYearsAgo}
        disabled={disabled}
        required={required}
        placeholderText={placeholder}
        className="w-full"
        wrapperClassName="w-full"
        popperClassName="react-datepicker-popper"
      />
    </div>
  );
};

export default ImprovedDatePicker;
